import { useCallback } from "react"

import {
  transport,
  useTransportMutation,
  useTransportQueryUtils,
} from "@/transport"

import type { SubmitUploadedConversationRequest } from "@/types_api/backend/transport/submit_uploaded_conversation_request"
import type { SubmitUploadedConversationResponse } from "@/types_api/backend/transport/submit_uploaded_conversation_response"
import type { UploadTracesForUploadedConversationRequest } from "@/types_api/backend/transport/upload_traces_for_uploaded_conversation_request"
import type { UploadTracesForUploadedConversationResponse } from "@/types_api/backend/transport/upload_traces_for_uploaded_conversation_response"

interface SubmitConversationInput {
  transcript?: SubmitUploadedConversationRequest["transcript"]
  audioUrl?: string
  audioS3Reference?: {
    s3Key: string
    s3Bucket: string
  }
  metadata?: Record<string, string>
  occurred_at?: string
  dev_id?: string
}

interface UploadConversationTracesInput {
  conversationId: string
  tracesData: Record<string, unknown>
}

interface SubmitConversationResult {
  conversationId: string
  status: string
  createdAt: string
}

interface UploadConversationTracesResult {
  success: boolean
}

function toSubmitUploadedConversationRequest(
  input: SubmitConversationInput,
): SubmitUploadedConversationRequest {
  return {
    transcript: input.transcript,
    audioUrl: input.audioUrl,
    audioS3Reference: input.audioS3Reference
      ? {
          s3Key: input.audioS3Reference.s3Key,
          s3Bucket: input.audioS3Reference.s3Bucket,
        }
      : undefined,
    metadata: input.metadata,
    occurred_at: input.occurred_at,
    dev_id: input.dev_id,
  }
}

function toUploadTracesForUploadedConversationRequest(
  input: UploadConversationTracesInput,
): UploadTracesForUploadedConversationRequest {
  return {
    conversationId: input.conversationId,
    tracesData: input.tracesData,
  }
}

function mapSubmitUploadedConversationResponse(
  response: SubmitUploadedConversationResponse,
): SubmitConversationResult {
  return {
    conversationId: response.conversation_id,
    status: response.status,
    createdAt: response.created_at,
  }
}

function mapUploadTracesForUploadedConversationResponse(
  response: UploadTracesForUploadedConversationResponse,
): UploadConversationTracesResult {
  return {
    success: response.success,
  }
}

export function useSubmitConversationTransport() {
  const queryUtils = useTransportQueryUtils()
  const mutation = useTransportMutation(
    transport.operations.submitUploadedConversation,
    {
      onMutate: () => queryUtils,
      onSettled: async (_data, _error, _variables, submittedQueryUtils) => {
        if (!submittedQueryUtils) {
          return
        }
        await Promise.all([
          submittedQueryUtils.invalidateResource(
            "uploaded-conversation-results",
          ),
          submittedQueryUtils.invalidateResource(
            "uploaded-conversations-metadata",
          ),
        ])
      },
    },
  )

  const mutateAsync = useCallback(
    async (input: SubmitConversationInput) =>
      mapSubmitUploadedConversationResponse(
        await mutation.mutateAsync(toSubmitUploadedConversationRequest(input)),
      ),
    [mutation],
  )

  return {
    ...mutation,
    mutateAsync,
  }
}

export function useUploadConversationTracesTransport() {
  const mutation = useTransportMutation(
    transport.operations.uploadTracesForUploadedConversation,
  )

  const mutateAsync = useCallback(
    async (input: UploadConversationTracesInput) =>
      mapUploadTracesForUploadedConversationResponse(
        await mutation.mutateAsync(
          toUploadTracesForUploadedConversationRequest(input),
        ),
      ),
    [mutation],
  )

  return {
    ...mutation,
    mutateAsync,
  }
}
